import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'formik';
import { EbolDetails, EbolPhoto } from 'src/api/types/EbolDetails';
import { Stack, Typography } from '@mui/material';
import ModalWindowContent from '@ui-lib/components/ModalWindow/ModalWindowContent';
import Autocomplete from '@ui-lib/components/Inputs/Autocomplete';
import FormikTextField from '@ui-lib/components/Inputs/TextField/FormikTextField';
import { PhotoPreview } from '../../../../../../../../components/PhotoPreview';
import { PHOTO_TYPE } from '../../../../../../../../api/types/PhotoType';
import { EBOL_ACTION_REASON, EBOL_ACTION_REASON_SOURCE, EbolActionFormValues, EbolActionReasonOption } from '../../types';
import { ebolActionModalDescriptions, ebolActionModalTitles, FIELD_NAMES, actionReasonLabels, ebolActionReasonPlaceholders, detailsRows, maxDetailsLength, ebolActionDetailsPlaceholders } from '../../const';
import AddPhotoButton from './components/AddPhotoButton';
import AddPhotoDescription from './components/AddPhotoDescription';

interface Props {
  onClose: () => void;
  status: EBOL_ACTION_REASON_SOURCE;
  ebolDetails: EbolDetails;
}

const EbolActionModalContent = connect<Props, EbolActionFormValues>(({ formik, status, onClose, ebolDetails }) => {
  const { t } = useTranslation();

  const {
    dirty,
    isSubmitting,
    handleSubmit,
    values,
    setFieldValue,
  } = formik;

  const reasonsOptions: EbolActionReasonOption[] = Object.values(EBOL_ACTION_REASON).map((reason) => ({
    label: t(actionReasonLabels[reason]),
    value: reason,
  }));

  return (
    <ModalWindowContent
      title={t(ebolActionModalTitles[status])}
      submitDisabled={!dirty || isSubmitting}
      submitBtnLabel={t('confirm')}
      onSubmit={handleSubmit}
      modalWidth='480px'
      modalMaxHeight='524px'
      onClose={onClose}
    >
      <Typography variant='body2' mb={5}>
        {t(ebolActionModalDescriptions[status])}
      </Typography>
      <Stack spacing={2}>
        <Autocomplete
          // @ts-expect-error: `placeholder` property does not exist in HTMLAttributes anymore
          placeholder={`${t(ebolActionReasonPlaceholders[status])} *`}
          options={reasonsOptions}
          name={FIELD_NAMES.REASONS}
          helperText={t('label_selectAtLeastReason', { count: 1 })}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          withFormik
          multiple
          required />
        <FormikTextField
          placeholder={t(ebolActionDetailsPlaceholders[status])}
          name={FIELD_NAMES.DETAILS}
          rows={detailsRows}
          maxLength={maxDetailsLength}
          multiline
        />
        <AddPhotoDescription status={status} />
        {values.photos.length > 0 && (
          <Stack>
            <Typography variant='overlineBold'>
              {t('label_assignedPhotos', { count: values.photos.length })}
            </Typography>
            <PhotoPreview photos={values.photos} createdAt={ebolDetails.created_at} />
          </Stack>
        )}
        <AddPhotoButton
          setPhoto={(photo) => {
            const newPhoto: EbolPhoto = {
              id: Date.now(),
              picture_url: photo,
              photo_type: PHOTO_TYPE.ACTUAL,
            };
            setFieldValue(FIELD_NAMES.PHOTOS, [...values.photos, newPhoto]);
          }} />
      </Stack>
    </ModalWindowContent>
  );
});

export default EbolActionModalContent;